import { useUnmountEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { Fragment, memo, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";

import { AppFooter } from "scmp-app/components/app-footer";
import { ArticleCommentsDrawer } from "scmp-app/components/article/article-comments-drawer";
import { ArticleDivider } from "scmp-app/components/article/article-divider";
import { ArticleFooter } from "scmp-app/components/article/article-footer";
import { ArticlePaywallUnlockProvider } from "scmp-app/components/article/article-paywall-unlock/contexts";
import { PianoPaywall } from "scmp-app/components/article/article-piano-paywall";
import { useArticlePianoPaywallContext } from "scmp-app/components/article/article-piano-paywall/contexts";
import { ArticleRender } from "scmp-app/components/article/article-render";
import { checkIsStyleArticle } from "scmp-app/components/article/article-render/style-article/helper";
import { ArticleSpeechContextProvider } from "scmp-app/components/article/article-speech/contexts";
import { ContentReadMoreContextProvider } from "scmp-app/components/content/content-read-more";
import { InlineOutstreamAdSlotContextProvider } from "scmp-app/components/schema-render/common/inline-ad-slot/outstream/contexts";
import { TextToSpeechPlayer } from "scmp-app/components/text-to-speech-player";
import type { AiSeoOverride } from "scmp-app/lib/ai-seo/types";
import { appAtom } from "scmp-app/lib/app/atoms";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import { useRosettaContext } from "scmp-app/lib/rosetta/contexts";
import { useSoftPaywallArticleTracker } from "scmp-app/lib/rosetta/features/soft-paywall/hooks";
import type { ShareContentUnlocked } from "scmp-app/pages/article/types";
import type {
  articleListContent$data,
  articleListContent$key,
} from "scmp-app/queries/__generated__/articleListContent.graphql";

import { ArticleListContextProvider } from "./contexts";
import {
  useArticlePagination,
  useArticleRecommendation,
  useContentPaginationChecker,
  useHandlePaginationNavigationHistory,
  useOnlyOnePlayingPlayer,
  useTrackPaginationCurrentContent,
} from "./hooks";
import { Item, StyledLoading } from "./styles";

type Item = articleListContent$data;

type Props = {
  aiSeoOverride?: AiSeoOverride;
  reference: articleListContent$key;
  shareContentUnlocked?: ShareContentUnlocked;
};

export const ArticleList: FunctionComponent<Props> = ({
  aiSeoOverride,
  reference,
  shareContentUnlocked,
}) => {
  const content = useFragment(
    graphql`
      fragment articleListContent on Content
      @argumentDefinitions(
        customContents: { type: "[CustomContentInput]", defaultValue: [] }
        bodyType: { type: "BodyFormatTypeEnum", defaultValue: PARAGRAPH }
      ) {
        __typename
        entityId
        ...articleDividerArticle
        ...articleFooterContent
        ...articlePaginationCheckerContent
        ...articlePaginationNavigationHistoryContent
          @arguments(customContents: $customContents, bodyType: $bodyType)
        ...articlePianoPaywallContent
        ...articleRecommendationContent
        ...articleRenderArticle @arguments(customContents: $customContents, bodyType: $bodyType)
        ...contextsArticleListContextProviderArticle
        ...contextsContentReadMoreContextProviderContent
        ...helpersCheckIsStyleArticle
      }
    `,
    reference,
  );

  const { navigatorInfo } = useAtomValue(appAtom);
  const { articleRecommendation, isLoading: isLoadingRecommendation } =
    useArticleRecommendation(content);
  const { data, hasNext, isLoading, loadNext } = useArticlePagination(
    articleRecommendation?.results.infiniteScrollArticleIds ?? [],
    articleRecommendation.results.infiniteScrollArticleMap,
  );
  const { articlePianoPaywallStateMap, resetArticlePianoPaywallStateMap } =
    useArticlePianoPaywallContext();

  const items = useMemo(() => [content, ...data] as [Item, ...Item[]], [content, data]);

  const { bindItemReference, currentContent, itemElementReferences } =
    useTrackPaginationCurrentContent(items);
  const { shouldStopPagination } = useContentPaginationChecker(items);

  const { softPaywall } = useRosettaContext();
  const softPaywallEventBus = softPaywall?.eventBus ?? null;

  const activeArticleElement = useMemo(() => {
    const activeArticleIndex = items.indexOf(currentContent);
    if (activeArticleIndex === -1) return null;
    const element = itemElementReferences.current[activeArticleIndex];
    return typeof HTMLElement !== "undefined" && element instanceof HTMLElement ? element : null;
  }, [items, currentContent, itemElementReferences]);

  useSoftPaywallArticleTracker({
    activeArticleElement,
    eventBus: softPaywallEventBus,
  });

  const footerVariant = useMemo(() => {
    const lastItem = last(items);
    return lastItem && checkIsStyleArticle(lastItem) ? "magazines-style" : undefined;
  }, [items]);

  const isLoadingPaywall = useMemo(() => {
    // Video articles don't have a paywall, skip loading check
    if (currentContent.__typename === "Video") return false;

    const doesNotRequirePaywall =
      articlePianoPaywallStateMap?.[currentContent.entityId] === "paywallNotRequired";
    if (doesNotRequirePaywall) return false;

    return !(articlePianoPaywallStateMap?.[currentContent.entityId] ?? "").startsWith("shown");
  }, [currentContent.__typename, currentContent.entityId, articlePianoPaywallStateMap]);

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage: hasNext,
    isLoading: isLoading || isLoadingPaywall,
    onLoadMore: loadNext,
  });

  useHandlePaginationNavigationHistory(currentContent, articleRecommendation);
  useUnmountEffect(() => resetArticlePianoPaywallStateMap?.());
  useOnlyOnePlayingPlayer();

  const isBot = navigatorInfo?.isBot ?? true; // Prevent infinite scroll for bots in the beginning
  const shouldContinuePagination =
    !isBot && !shouldStopPagination && (hasNext || isLoadingRecommendation);

  const renderFooter = useCallback(() => {
    if (shouldContinuePagination) return <ArticleDivider reference={content} />;

    return (
      <>
        {!isBot && (
          <ArticleFooter
            articleRecommendation={{
              allArticleIds: articleRecommendation.allArticleIds,
              articleIds: articleRecommendation.results.moreOnThisTopicWidgetIds,
              recommendationId: articleRecommendation.recommendationId,
            }}
            divider={<ArticleDivider reference={content} />}
            reference={content}
          />
        )}
        <AppFooter variant={footerVariant} />
      </>
    );
  }, [
    articleRecommendation.allArticleIds,
    articleRecommendation.recommendationId,
    articleRecommendation.results.moreOnThisTopicWidgetIds,
    content,
    footerVariant,
    isBot,
    shouldContinuePagination,
  ]);

  return (
    <InlineOutstreamAdSlotContextProvider>
      <ArticleListContextProvider reference={content}>
        <ArticlePaywallUnlockProvider shareContentUnlocked={shareContentUnlocked}>
          <ArticleSpeechContextProvider>
            <ArticleCommentsDrawer />
            {items.map((item, index) => (
              <Fragment key={index}>
                {index > 0 && <ArticleDivider reference={content} />}
                <Item ref={bindItemReference(index)}>
                  <ContentReadMoreContextProvider reference={item}>
                    <MemorizedArticleRender
                      aiSeoOverride={index === 0 ? aiSeoOverride : undefined}
                      reference={item}
                    />
                  </ContentReadMoreContextProvider>
                </Item>
                <ClientSideSuspense>
                  {item.__typename !== "Video" && <PianoPaywall reference={item} />}
                </ClientSideSuspense>
              </Fragment>
            ))}

            <ClientSideSuspense>{renderFooter()}</ClientSideSuspense>

            {/* hide the loader using css to prevent intersection observer issues due to mount/unmount */}
            <StyledLoading
              $isHidden={!shouldContinuePagination}
              ref={infiniteScrollTriggerReference}
            />

            <TextToSpeechPlayer />
          </ArticleSpeechContextProvider>
        </ArticlePaywallUnlockProvider>
      </ArticleListContextProvider>
    </InlineOutstreamAdSlotContextProvider>
  );
};

const MemorizedArticleRender = memo(ArticleRender);

ArticleList.displayName = "ArticleList";
